import { getBithumbUsdtRateState } from '@/services/bithumbRate'
import {
  estimateUsdtTransferEnergy,
  USDT_TRANSFER_BANDWIDTH_BYTES
} from '@/utils/tronUsdt'

const POLL_MS = 10000
const TRONGRID_CHAIN_PARAMS_API = 'https://api.trongrid.io/wallet/getchainparameters'
const BITHUMB_TRX_TICKER = 'https://api.bithumb.com/public/ticker/TRX_KRW'
const DEFAULT_ENERGY_USED = 64285
const DEFAULT_ENERGY_SUN = 100
const DEFAULT_TX_SUN_PER_BYTE = 1000
const SUN_PER_TRX = 1000000

let state = {
  feeTrx: null,
  feeUsdt: null,
  feeKrw: null,
  energyUsed: DEFAULT_ENERGY_USED,
  energySun: DEFAULT_ENERGY_SUN,
  bandwidthBytes: USDT_TRANSFER_BANDWIDTH_BYTES,
  trxKrwRate: null,
  loading: false,
  ready: false,
  error: '',
  updatedAt: null
}

const listeners = new Set()
let pollTimer = null
let fetchPromise = null

function notify () {
  const snapshot = { ...state }
  listeners.forEach(fn => {
    try {
      fn(snapshot)
    } catch (err) {
      // ignore listener errors
    }
  })
}

function parseChainParam (body, key, fallback) {
  const params = body && Array.isArray(body.chainParameter) ? body.chainParameter : []
  const found = params.find(item => item && item.key === key)
  const value = Number(found ? found.value : NaN)
  return Number.isFinite(value) && value > 0 ? value : fallback
}

function computeFeeFromResources ({
  energyUsed,
  energySun,
  bandwidthBytes,
  txSunPerByte,
  trxKrwRate,
  usdtKrwRate
}) {
  const energyCostSun = energyUsed * energySun
  const bandwidthCostSun = bandwidthBytes * txSunPerByte
  const feeSun = energyCostSun + bandwidthCostSun
  const feeTrx = feeSun / SUN_PER_TRX

  let feeKrw = null
  let feeUsdt = null

  if (Number.isFinite(trxKrwRate) && trxKrwRate > 0) {
    feeKrw = feeTrx * trxKrwRate
  }
  if (feeKrw != null && Number.isFinite(usdtKrwRate) && usdtKrwRate > 0) {
    feeUsdt = feeKrw / usdtKrwRate
  }

  return { feeTrx, feeUsdt, feeKrw }
}

export function isTronNetworkFeeReady (snapshot = state) {
  const fee = Number(snapshot.feeUsdt)
  return snapshot.ready === true && Number.isFinite(fee) && fee > 0
}

async function fetchChainFeeParams () {
  let energySun = DEFAULT_ENERGY_SUN
  let txSunPerByte = DEFAULT_TX_SUN_PER_BYTE

  try {
    const res = await fetch(TRONGRID_CHAIN_PARAMS_API, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: '{}'
    })
    if (res.ok) {
      const body = await res.json()
      energySun = parseChainParam(body, 'getEnergyFee', DEFAULT_ENERGY_SUN)
      txSunPerByte = parseChainParam(body, 'getTransactionFee', DEFAULT_TX_SUN_PER_BYTE)
    }
  } catch (err) {
    // keep defaults
  }

  return { energySun, txSunPerByte }
}

async function fetchTronNetworkFee () {
  const usdtKrwRate = getBithumbUsdtRateState().rate
  state.loading = true
  state.error = ''
  notify()

  try {
    const [energyUsed, chainFees, trxRes] = await Promise.all([
      estimateUsdtTransferEnergy(),
      fetchChainFeeParams(),
      fetch(BITHUMB_TRX_TICKER, { headers: { Accept: 'application/json' } })
    ])

    let trxKrwRate = null
    if (trxRes.ok) {
      const trxBody = await trxRes.json()
      const closing = trxBody && trxBody.data ? trxBody.data.closing_price : null
      trxKrwRate = Number(closing)
      if (!Number.isFinite(trxKrwRate) || trxKrwRate <= 0) {
        trxKrwRate = null
      }
    }

    const resolvedEnergyUsed = energyUsed || DEFAULT_ENERGY_USED
    const fees = computeFeeFromResources({
      energyUsed: resolvedEnergyUsed,
      energySun: chainFees.energySun,
      bandwidthBytes: USDT_TRANSFER_BANDWIDTH_BYTES,
      txSunPerByte: chainFees.txSunPerByte,
      trxKrwRate,
      usdtKrwRate
    })

    state.energyUsed = resolvedEnergyUsed
    state.energySun = chainFees.energySun
    state.bandwidthBytes = USDT_TRANSFER_BANDWIDTH_BYTES
    state.trxKrwRate = trxKrwRate
    state.feeTrx = fees.feeTrx
    state.feeUsdt = fees.feeUsdt
    state.feeKrw = fees.feeKrw
    state.updatedAt = Date.now()
    state.loading = false

    if (fees.feeUsdt == null) {
      state.error = 'fee_unavailable'
      state.ready = false
    } else {
      state.error = ''
      state.ready = true
    }
  } catch (err) {
    state.loading = false
    if (state.feeUsdt == null) {
      state.error = 'fee_fetch_failed'
      state.ready = false
    }
  }

  notify()
}

function refreshTronNetworkFee () {
  if (fetchPromise) return fetchPromise
  fetchPromise = fetchTronNetworkFee().finally(() => {
    fetchPromise = null
  })
  return fetchPromise
}

function startPolling () {
  if (pollTimer) return
  refreshTronNetworkFee()
  pollTimer = window.setInterval(() => {
    refreshTronNetworkFee()
  }, POLL_MS)
}

function stopPolling () {
  if (pollTimer) {
    window.clearInterval(pollTimer)
    pollTimer = null
  }
}

/** @returns {function} unsubscribe */
export function subscribeTronNetworkFee (callback) {
  listeners.add(callback)
  callback({ ...state })
  if (listeners.size === 1) startPolling()
  return () => {
    listeners.delete(callback)
    if (listeners.size === 0) stopPolling()
  }
}

export function getTronNetworkFeeState () {
  return { ...state }
}

export function refreshNetworkFeeNow () {
  return refreshTronNetworkFee()
}
